import { useState, useEffect } from "react";
import { Star, ThumbsUp, MessageSquare, Send } from "lucide-react";
import { Card, Tabs, Button, Input, Modal } from "antd";
import "./StudentReviews.css";
import { useUserInfo } from "GlobalStates/User";
import apiClient from "QueryClient/axiosClient";

const { TabPane } = Tabs;
const { TextArea } = Input;

export const StudentReviews = ({ data }) => {
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await apiClient.get(
          `/universityDetails/${data.id}/qa`
        );
        setQaList(response.data);
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [data.id]);

  function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  function seededRandom(seed) {
    return function () {
      seed += 0x6d2b79f5;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function generateCategories(collegeName, overallRating) {
    overallRating = parseFloat(overallRating);
    const seed = hashString(collegeName);
    const rand = seededRandom(seed);

    const categories = [
      "Academics",
      "Campus Life",
      "Facilities",
      "Career Support",
      "Value for Money",
    ];

    return categories.map((category) => {
      let variation = (rand() - 0.5) * 0.4; // ±0.2
      let rating = Math.min(
        5,
        Math.max(3, parseFloat((overallRating + variation).toFixed(1)))
      );
      let percentage = Math.round((rating / 5) * 100);
      return { name: category, rating, percentage };
    });
  }

  const categoricalRatings = generateCategories(data.Name, data.Rating);

  const ratings = {
    overall: data.Rating,
    categories: categoricalRatings,
  };

  const testimonials = data.Student_Reviews;

  const renderStars = (count, total = 5) => {
    return Array(total)
      .fill(0)
      .map((_, index) => (
        <Star
          key={index}
          className={`star-icon ${index < count ? "filled" : "unfilled"}`}
        />
      ));
  };

  const { user } = useUserInfo();
  const [question, setQuestion] = useState("");
  const [qaList, setQaList] = useState([]);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [answerInput, setAnswerInput] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSubmitQuestion = async () => {
    if (!question.trim()) return;

    try {
      const response = await apiClient.post(
        `/universityDetails/${data.id}/qa`,
        {
          question,
          asker: user?.name || "Anonymous",
        }
      );

      setQaList((prev) => [response.data, ...prev]);
      setQuestion("");
    } catch (error) {
      console.error("Error submitting question:", error);
    }
  };

  const openAnswerModal = (item) => {
    setActiveQuestion(item);
    setAnswerInput(item.answer || "");
    setIsModalOpen(true);
  };

  const handleAnswerSubmit = async () => {
    if (!data?._id || !activeQuestion?._id) return;

    try {
      const response = await apiClient.put(
        `/universityDetails/${data.id}/qa/${activeQuestion._id}/answer`,
        {
          answer: answerInput,
          responderName: user?.name || "Anonymous",
          responderRole: "Community Member",
        }
      );

      // Replace entire question object
      setQaList((prev) =>
        prev.map((qa) => (qa._id === activeQuestion._id ? response.data : qa))
      );

      setIsModalOpen(false);
      setAnswerInput("");
    } catch (error) {
      console.error("Error submitting answer:", error);
    }
  };

  const incrementHelpful = async (questionId, answerId) => {
    try {
      const response = await apiClient.put(
        `/universityDetails/${data.id}/qa/${questionId}/answers/${answerId}/helpful`
      );

      const updatedAnswer = response.data;

      setQaList((prev) =>
        prev.map((qa) =>
          qa._id === questionId
            ? {
                ...qa,
                answers: qa.answers.map((ans) =>
                  ans._id === updatedAnswer._id ? updatedAnswer : ans
                ),
              }
            : qa
        )
      );
    } catch (error) {
      console.error("Error incrementing helpful count:", error);
    }
  };

  return (
    <Card className="student-reviews-card">
      <div className="student-reviews-header">
        <h2 className="student-reviews-title">Student Reviews & Q&A</h2>
      </div>
      <div className="student-reviews-content">
        <Tabs defaultActiveKey="reviews" className="student-reviews-tabs">
          <TabPane tab="Reviews" key="reviews">
            <div className="reviews-container">
              <div className="reviews-grid">
                <div className="ratings-section">
                  <div className="overall-rating">
                    <div className="rating-score">
                      <Star className="large-star filled" />
                      <span className="ratingValue">{ratings.overall}</span>
                    </div>
                  </div>

                  <div className="category-ratings">
                    {ratings.categories.map((category, index) => (
                      <div key={index} className="category-item">
                        <div className="category-header">
                          <span className="category-name">{category.name}</span>
                          <div className="category-stars">
                            {renderStars(category.rating)}
                          </div>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${category.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="testimonials-section">
                  <h3 className="section-title">Student Testimonials</h3>
                  <div className="testimonials-list">
                    {testimonials.map((testimonial, index) => (
                      <div key={index} className="testimonial-item">
                        <div className="testimonial-header">
                          <div className="student-info">
                            <div className="student-image-placeholder">
                              [img]
                            </div>
                            <div>
                              <h4 className="student-name">
                                {/* {testimonial.studentName} */}
                                {"Anonymous Student"}
                              </h4>
                              {/* <p className="student-details">
                                {testimonial.major}, {testimonial.classYear}
                              </p> */}
                            </div>
                          </div>
                          {/* <div className="testimonial-rating">
                            {renderStars(testimonial.rating)}
                          </div> */}
                        </div>
                        <p className="testimonial-text">{testimonial}</p>
                        <div className="testimonial-footer">
                          {/* <span className="timestamp">
                            {testimonial.timestamp}
                          </span> */}
                          {/* <Button className="helpful-button">
                            <ThumbsUp className="helpful-icon" />
                            <span>Helpful ({testimonial.helpfulCount})</span>
                          </Button> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button className="view-all-button">View All Reviews</Button>
                </div>
              </div>
            </div>
          </TabPane>

          <TabPane tab="Q&A" key="qa">
            <div className="qa-container">
              <div className="ask-question-section">
                <h3 className="section-title">Ask a Question</h3>
                <div className="question-form">
                  <TextArea
                    placeholder="What would you like to know about?"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    className="question-textarea"
                  />
                  <Button
                    onClick={handleSubmitQuestion}
                    className="submit-question-button"
                    type="primary"
                  >
                    <Send className="submit-icon" />
                    Submit Question
                  </Button>
                </div>
              </div>

              <div className="community-qa-section">
                <h3 className="section-title">Community Q&A</h3>
                <div className="qa-list">
                  {qaList.map((item) => (
                    <div key={item._id} className="qa-item">
                      <div
                        className="question-header"
                        onClick={() => openAnswerModal(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <MessageSquare className="question-icon" />
                        <div>
                          <h4 className="question-text">{item.question}</h4>
                          <p className="question-meta">
                            Asked by {item.asker} •{" "}
                            {`${
                              new Date(item.timestamp).getMonth() + 1
                            }/${new Date(item.timestamp).getFullYear()}`}
                          </p>
                        </div>
                      </div>

                      {item.answers.map((ans) => (
                        <div key={ans._id} className="answer-section">
                          <p className="answer-text">{ans.text}</p>
                          <div className="answer-footer">
                            <div className="responder-info">
                              <div className="responder-image-placeholder">
                                [img]
                              </div>
                              <div>
                                <span className="responder-name">
                                  {ans.responder?.name || "Anonymous"}
                                </span>
                                <span className="responder-role">
                                  {" "}
                                  • {ans.responder?.role || "Community Member"}
                                </span>
                              </div>
                            </div>
                            <Button
                              className="helpful-button"
                              onClick={() =>
                                incrementHelpful(item._id, ans._id)
                              }
                            >
                              <ThumbsUp className="helpful-icon" />
                              <span>Helpful ({ans.helpfulCount || 0})</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {qaList.length > 3 && (
                  <Button className="view-all-button">
                    View All Questions
                  </Button>
                )}
              </div>

              <Modal
                title={
                  activeQuestion?.answer ? "Edit Answer" : "Answer Question"
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleAnswerSubmit}
                okText="Submit Answer"
              >
                <p>
                  <strong>Q:</strong> {activeQuestion?.question}
                </p>
                <TextArea
                  placeholder="Type your answer here..."
                  rows={4}
                  value={answerInput}
                  onChange={(e) => setAnswerInput(e.target.value)}
                />
              </Modal>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Card>
  );
};
